import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { scrollStyle } from './Common.styles';
import { OccupyFreeSpace } from './ActionRow';

type BackgroundOptions = "none" | "panel" | "none-padded";

interface SimpleFormProps {
    title?: string | ReactNode;
    headerItems?: ReactNode;
    subHeaderItems?: ReactNode;
    preBodyItems?: ReactNode;
    children?: ReactNode;
    formBodyProps?: React.HTMLAttributes<HTMLDivElement>;
    formHeaderProps?: React.HTMLAttributes<HTMLDivElement>;
    fitFullHeight?: boolean;
    allowTitleWrap?: boolean;
    className?: string;
    noTitle?: boolean;
    background?: BackgroundOptions;
    headerBackground?: BackgroundOptions;
    dimmed?: boolean;
}

export const Form2 = (props: SimpleFormProps) => {
  const { title, headerItems, subHeaderItems, children, formBodyProps, formHeaderProps, dimmed } = props;

  let formPaperStyle = {};
  let formBodyStyle = formBodyProps?.style || {};
  let formHeaderStyle = formHeaderProps?.style || undefined;

    if(props.fitFullHeight) {
      formPaperStyle = {
          display: 'flex',
          flexFlow: 'column',
          height: "100%",
          maxHeight: "100%", // Ensures it doesn't push past the parent
          overflow: "hidden", // Prevents the wrapper itself from scrolling
          ...formPaperStyle
      };
      formBodyStyle = {
          flex: "1 1 0px", // flex-grow: 1, flex-shrink: 1, flex-basis: 0px
          overflowY: 'auto', // Allows the body to scroll
          minHeight: 0, // CRITICAL flexbox hack: allows the container to shrink smaller than its content
          ...formBodyStyle,
      }
  }

  return (
    <Form2Wrapper className={`form-wrapper form2-wrapper ${props.className}`} style={formPaperStyle}>
      <FormHeader2
        {...formHeaderProps}
        className="form-header"
        background={props.headerBackground || props.background}
        dimmed={dimmed}
        style={formHeaderStyle}>
        <FormHeader2Inner>
          {!props.noTitle && <FormTitle allowWrap={props.allowTitleWrap}>{title}</FormTitle>}

          {headerItems}
        </FormHeader2Inner>
        {subHeaderItems}
      </FormHeader2>

      {props.preBodyItems}

      <FormBody2
        className="form-body"
        {...formBodyProps}
        background={props.background}
        dimmed={dimmed}
        style={formBodyStyle}>
        {children}
      </FormBody2>
    </Form2Wrapper>
  );
}


export const Form2Wrapper = styled.div`
  width: 100%;
  min-height: 100%;

  box-sizing: border-box;
`;

const FormTitle = styled(Typography)<{ allowWrap?: boolean}>`
  white-space: ${props => props.allowWrap ? "initial" : "nowrap"};
`;
FormTitle.defaultProps = { variant: 'h4' };


export const FormHeader2Inner = styled.div<{ alignItems?: React.CSSProperties["alignItems"]}>`
  display: flex;
  flex-flow: row;
  align-items: ${props => props.alignItems || "center"};

  gap: 0.5rem;

  ${props => props.theme.breakpoints.down("sm")} {
    flex-flow: column;
    align-items: flex-start;

    & ${OccupyFreeSpace}, & .hidden-phones {
      display: none;
    }
  }
`;

const formPanelCss = (theme: any, background: SimpleFormProps["background"], dimmed: boolean) =>
  css`
    ${background === "panel" &&
      `background: ${theme.palette.background.default};
       border-radius: ${theme.shape.borderRadius*2}px;
       box-shadow: 0px 0px 5px -1px #33333340;`}
    
    ${(background === "panel" || background === "none-padded") &&
      `padding: 0.5rem;`}

    /* dimming logic */
      transition: opacity 0.2s ease-in-out;
      opacity: ${dimmed ? 0.75 : 1};
  `;

export const FormHeader2 = styled.div<{ 
  background?: SimpleFormProps["background"]; 
  dimmed?: boolean; 
}>`
    ${props => formPanelCss(props.theme, props.background, props.dimmed || false)}
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    display: flex;
    flex-flow: column;
    align-items: stretch;

    gap: 0.5rem;
    margin-bottom: 10px;
`;

export const FormBody2 = styled.div<{ 
  background?: SimpleFormProps["background"]; 
  dimmed?: boolean; 
}>`
  ${props => formPanelCss(props.theme, props.background, props.dimmed || false)}

  display: flex;
  flex-flow: column;

  ${props => scrollStyle(props.theme, { size: 5 })}
`;


export interface GlassChipProps {
  pill?: boolean;
  dense?: boolean;
}

export const GlassChip = styled.div<GlassChipProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  /* Размеры и отступы */
  padding: ${({ dense }) => (dense ? '2px 8px' : '4px 10px')};
  border-radius: ${({ pill }) => (pill ? '9999px' : '6px')};
  
  /* Типографика */
  font-family: inherit;
  font-size: 0.8125rem; /* ~13px */
  font-weight: 500;
  line-height: 1.4;
  color: #1a1918; /* Глубокий темный цвет текста */
  letter-spacing: -0.01em;

  /* Эффект матового стекла (Frosted Glass) */
  background-color: rgba(255, 255, 255, 0.52);
  backdrop-filter: blur(12px) saturate(130%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);

  /* Граница: видна на темном и отделяет от чисто белого фона */
  border: 1px solid rgba(255, 255, 255, 0.55);

  /* Тени:
     1. Внутренний блик сверху (создает объем)
     2. Мягкая внешняя тень (не дает слиться с чисто белым фоном)
  */
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.6),
    0 2px 6px -1px rgba(0, 0, 0, 0.08),
    0 1px 3px -1px rgba(0, 0, 0, 0.05);

  /* Плавный переход при ховере (если чип кликабельный) */
  transition: background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
  user-select: none;

  /* Опциональные стили при использовании в качестве кнопки */
  &[role='button'],
  &:hover {
    background-color: rgba(255, 255, 255, 0.92);
    box-shadow: 
      inset 0 1px 1px 0 rgba(255, 255, 255, 0.9),
      0 4px 10px -2px rgba(0, 0, 0, 0.12);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.8);
    transform: scale(0.98);
  }

  /* Для иконок внутри */
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.85;
    flex-shrink: 0;
  }
`;
